import React, { useState, useEffect, useRef } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Toast } from '../../../services/Toast';
import { useDispatch, useSelector } from 'react-redux'
import {
  getCategoryById,
  putCategoryById,
  listProductsInCategoryById,
} from '../../../actions/categoryActions'
import AdminActionBar from '../../../components/AdminActionBar'
const apiUrl = process.env.REACT_APP_API_URL;
const CategoryEditScreen = ({ match }) => {
  const pId = match.params.id
  const dispatch = useDispatch()

  const [searchedProducts, setSearchedProducts] = useState([])
  const [isSearching, setIsSearching] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false)
  const [modalSearch, setModalSearch] = useState('')
  const [modalResults, setModalResults] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [categoryName, setCategoryName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState([])
  const [categoryId, setCategoryId] = useState('')
  const [showOnHomepage, setShowOnHomepage] = useState(false)
  const [homepageOrder, setHomepageOrder] = useState(0)
  const [homepageProductLimit, setHomepageProductLimit] = useState(5)
  const [homepageProductSort, setHomepageProductSort] = useState('newest')
  const [localProducts, setLocalProducts] = useState([])
  const [sortMode, setSortMode] = useState('manual')

  const getCategory = useSelector((state) => state.getCategory)
  const { fetchCategory } = getCategory

  const putCategory = useSelector((state) => state.putCategory)
  const { loading: updateLoading, error: updateError, category } = putCategory

  const [dataChanged, setDataChanged] = useState(false)
  const [baselineReady, setBaselineReady] = useState(false)
  const baselineRef = useRef(null)
  const savedRef = useRef(false)

  // Slugs are always stored lowercase and hyphen-separated
  const slugify = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

  const normalizeComparable = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => normalizeComparable(item))
    }
    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          acc[key] = normalizeComparable(value[key])
          return acc
        }, {})
    }
    return value
  }

  const buildComparableState = () =>
    normalizeComparable({
      name: categoryName || '',
      slug: slug || '',
      description: description || '',
      tags: tags || [],
      showOnHomepage: showOnHomepage || false,
      homepageOrder: homepageOrder || 0,
      homepageProductLimit: homepageProductLimit || 5,
      homepageProductSort: homepageProductSort || 'newest',
    })

  const buildComparableFromApi = (cat) =>
    normalizeComparable({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      tags: cat.tags || [],
      showOnHomepage: cat.showOnHomepage || false,
      homepageOrder: cat.homepageOrder || 0,
      homepageProductLimit: cat.homepageProductLimit || 5,
      homepageProductSort: cat.homepageProductSort || 'newest',
    })

  const { userInfo } = useSelector((state) => state.userLogin) || {}
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo?.token}` }
  const [newProductAdded, setNewProductAdded] = useState(false)

  const listProductsInCategory = useSelector(
    (state) => state.listProductsInCategory
  )
  const {
    loading: listProductsLoading,
    error: listProductsError,
    products,
  } = listProductsInCategory


  function updateCategoryHandler() {
    const cleanSlug = slugify(slug || categoryName)
    if (cleanSlug !== slug) setSlug(cleanSlug)
    const updatedCategory = {
      name: categoryName,
      slug: cleanSlug,
      description: description,
      tags: tags,
      showOnHomepage: showOnHomepage,
      homepageOrder: homepageOrder,
      homepageProductLimit: homepageProductLimit,
      homepageProductSort: homepageProductSort,
    }

    savedRef.current = true
    dispatch(putCategoryById(categoryId, updatedCategory))
  }

  function searchHandler(e) {
    const { value } = e.target
    fetchProductsByName(value)
    setIsSearching(true);
  }


  const fetchProductsByName = async (name) => {
    if (name.trim() === '') {
      setSearchedProducts([]); // Clear results when query is empty
      setIsSearching(false); // Reset searching state
      return;
    }


    try {
      const response = await fetch(`${apiUrl}/api/products/name/${name}`)
      const data = await response.json()
      setSearchedProducts(data)
    } catch (error) {
      console.error('Error fetching products:', error)
    }
  }

  async function addProducts(productId) {
    try {
      const response = await fetch(`${apiUrl}/api/category/products/store/`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          productId: productId,
          categoryId: categoryId,
        }),
      });

      if (!response.ok) {
        // Handle the error response
        const errorData = await response.json(); // Extract error message from JSON response
        throw new Error(errorData.message || 'Failed to add product to category');
      }
      else {
        setNewProductAdded(true)
      }

    } catch (error) {
      Toast({ type: 'errror', message: error?.message });
    }
  }

  async function deleteHandler(productId) {
    try {
      const response = await fetch(`${apiUrl}/api/category/products/store/`, {
        method: 'DELETE',
        headers: authHeaders,
        body: JSON.stringify({
          productId: productId,
          categoryId: categoryId,
        }),
      });

      if (!response.ok) {
        // Handle the error response
        const errorData = await response.json(); // Extract error message from JSON response
        throw new Error(errorData.message || 'Failed to add product to category');
      }
      else {
        setNewProductAdded(true)
      }

    } catch (error) {
      Toast({ type: 'errror', message: error?.message });
    }
  }


  const handleModalSearch = async (value) => {
    setModalSearch(value)
    if (value.trim().length < 2) {
      setModalResults([])
      return
    }
    try {
      const response = await fetch(`${apiUrl}/api/products/name/${value}`)
      const data = await response.json()
      setModalResults(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching products:', error)
    }
  }

  const toggleProductSelection = (product) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p._id === product._id)
      if (exists) return prev.filter((p) => p._id !== product._id)
      return [...prev, product]
    })
  }

  const handleModalDone = async () => {
    for (const product of selectedProducts) {
      await addProducts(product._id)
    }
    setSelectedProducts([])
    setModalSearch('')
    setModalResults([])
    setShowProductModal(false)
  }

  const handleModalCancel = () => {
    setSelectedProducts([])
    setModalSearch('')
    setModalResults([])
    setShowProductModal(false)
  }

  const onDragEnd = async (result) => {
    if (!result.destination) return
    const items = Array.from(localProducts)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setLocalProducts(items)

    try {
      await fetch(`${apiUrl}/api/category/products/reorder`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          categoryId: categoryId,
          productIds: items.map((p) => p._id),
        }),
      })
    } catch (error) {
      console.error('Error reordering:', error)
    }
  }

  useEffect(() => {
    if (products?.products) {
      setLocalProducts(products.products)
    }
  }, [products])

  useEffect(() => {
    dispatch(getCategoryById(pId))
  }, [newProductAdded])


  useEffect(() => {
    if (fetchCategory) {
      setCategoryName(fetchCategory.name);
      setSlug(fetchCategory.slug);
      setDescription(fetchCategory.description);
      setTags(fetchCategory.tags);
      setCategoryId(fetchCategory._id)
      setShowOnHomepage(fetchCategory.showOnHomepage || false)
      setHomepageOrder(fetchCategory.homepageOrder || 0)
      setHomepageProductLimit(fetchCategory.homepageProductLimit || 5)
      setHomepageProductSort(fetchCategory.homepageProductSort || 'newest')
      baselineRef.current = buildComparableFromApi(fetchCategory)
      setBaselineReady(true)
      setDataChanged(false)
    }
  }, [fetchCategory]);

  // Mark the form dirty only when the current values differ from the loaded baseline
  useEffect(() => {
    if (!baselineReady || !baselineRef.current) return
    const isDirty =
      JSON.stringify(buildComparableState()) !==
      JSON.stringify(baselineRef.current)
    setDataChanged(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    baselineReady,
    categoryName,
    slug,
    description,
    tags,
    showOnHomepage,
    homepageOrder,
    homepageProductLimit,
    homepageProductSort,
  ]);

  // After a successful save: reset the baseline and toast (only on a real save)
  useEffect(() => {
    if (category && savedRef.current) {
      savedRef.current = false
      baselineRef.current = buildComparableState()
      setDataChanged(false)
      Toast({ type: 'success', message: 'Category updated successfully!' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Surface a failed save instead of silently doing nothing
  useEffect(() => {
    if (updateError && savedRef.current) {
      savedRef.current = false
      Toast({ type: 'error', message: updateError })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);


  useEffect(() => {
    dispatch(listProductsInCategoryById(slug))
  }, [slug])

  useEffect(() => {
    dispatch(listProductsInCategoryById(slug))
  }, [newProductAdded])

  return (
    <>
      <AdminActionBar
        loading={updateLoading}
        name={categoryName}
        dataChanged={dataChanged}
        saveHandler={updateCategoryHandler}
        saveLabel='Update'
        viewUrl={`${process.env.REACT_APP_SITE_URL}/categories/${slug}`}
        backFallback='/admin/categoryList'
      />
      <section className='mt-5 mb-5'>
        <div className='container'>
          <div className='row'>
            <div className='col-12 mb-5'>
              <h1 className='commonTitle--xl'>Edit {categoryName} Category </h1>
            </div>
            <div className='col-lg-12'>
              <div className='card mb-3'>
                <div className='card-body'>
                  <div className='mb-2'>
                    <label htmlFor='name' className='form-label'>
                      Name
                    </label>
                    <input
                      onChange={(e) => setCategoryName(e.target.value)}
                      value={categoryName}
                      type='text'
                      autoComplete='off'
                      name='name'
                      className='form-control'
                      id='name'
                      placeholder='Category Name'
                    />
                  </div>

                  <div className='mb-2'>
                    <label htmlFor='slug' className='form-label'>
                      Slug
                    </label>
                    <input
                      onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9-]/g, ''))}
                      value={slug}
                      type='text'
                      autoComplete='off'
                      name='slug'
                      className='form-control'
                      id='slug'
                      placeholder='Category Slug'
                    />
                  </div>

                  <div className='mb-2'>
                    <label htmlFor='description' className='form-label'>
                      Description
                    </label>
                    <input
                      onChange={(e) => setDescription(e.target.value)}
                      value={description}
                      type='text'
                      autoComplete='off'
                      name='description'
                      className='form-control'
                      id='description'
                      placeholder='A Description'
                    />
                  </div>


                  <hr className='my-4' />
                  <h5 className='mb-3'>Homepage Settings</h5>

                  <div className='mb-2 form-check'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id='showOnHomepage'
                      checked={showOnHomepage}
                      onChange={(e) => setShowOnHomepage(e.target.checked)}
                    />
                    <label className='form-check-label' htmlFor='showOnHomepage'>
                      Show on Homepage
                    </label>
                  </div>

                  {showOnHomepage && (
                    <div className='row'>
                      <div className='col-md-4 mb-2'>
                        <label htmlFor='homepageOrder' className='form-label'>
                          Display Order
                        </label>
                        <input
                          type='number'
                          className='form-control'
                          id='homepageOrder'
                          value={homepageOrder}
                          onChange={(e) => setHomepageOrder(Number(e.target.value))}
                        />
                      </div>

                      <div className='col-md-4 mb-2'>
                        <label htmlFor='homepageProductLimit' className='form-label'>
                          Products to Show
                        </label>
                        <input
                          type='number'
                          className='form-control'
                          id='homepageProductLimit'
                          value={homepageProductLimit}
                          onChange={(e) => setHomepageProductLimit(Number(e.target.value))}
                        />
                      </div>

                      <div className='col-md-4 mb-2'>
                        <label htmlFor='homepageProductSort' className='form-label'>
                          Sort Products By
                        </label>
                        <select
                          className='form-control'
                          id='homepageProductSort'
                          value={homepageProductSort}
                          onChange={(e) => setHomepageProductSort(e.target.value)}
                        >
                          <option value='newest'>Newest</option>
                          <option value='popular'>Popular</option>
                          <option value='priceAsc'>Price Low-High</option>
                          <option value='priceDesc'>Price High-Low</option>
                          <option value='discount'>Highest Discount</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <hr className='my-4' />

                  <div className='mb-3'>
                    <button
                      type='button'
                      className='btn btn-outline-primary'
                      onClick={() => setShowProductModal(true)}
                    >
                      <i className='fas fa-plus me-2'></i>Add Products
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >



      <section>
        <div className='container'>
          <div className='row'>
            <div className='col-12'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='mb-0'>Products ({localProducts.length})</h5>
              </div>
              {localProducts.length > 0 ? (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId='categoryProducts'>
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {localProducts.map((item, index) => (
                          <Draggable key={item._id} draggableId={item._id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className='d-flex align-items-center mb-2 p-2'
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging ? '#f0f7ff' : '#fff',
                                  border: '1px solid #e5e5e5',
                                  borderRadius: '8px',
                                }}
                              >
                                <span
                                  {...provided.dragHandleProps}
                                  style={{ cursor: 'grab', padding: '0 12px', color: '#999', fontSize: '18px' }}
                                >
                                  ⠿
                                </span>
                                <span className='me-3' style={{ color: '#999', minWidth: '30px' }}>{index + 1}.</span>
                                {item.image && item.image[0] ? (
                                  <img
                                    src={`${apiUrl}/${item.image[0]}`}
                                    alt=''
                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', marginRight: '12px' }}
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '40px', height: '40px', borderRadius: '6px', marginRight: '12px',
                                    backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <i className='fas fa-image' style={{ color: '#ccc' }}></i>
                                  </div>
                                )}
                                <span className='flex-grow-1' style={{ fontSize: '14px' }}>{item.name}</span>
                                {item.isActive !== false ? (
                                  <span className='badge bg-success me-3'>Active</span>
                                ) : (
                                  <span className='badge bg-secondary me-3'>Inactive</span>
                                )}
                                <button
                                  className='btn btn-sm'
                                  onClick={() => deleteHandler(item._id)}
                                  style={{ color: '#999', fontSize: '18px' }}
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <p className='text-muted'>No products</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {showProductModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onClick={handleModalCancel}
        >
          <div style={{
            background: '#fff', borderRadius: '12px', width: '550px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #e5e5e5' }}>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='mb-0'>Add Products</h5>
                <button type='button' className='btn-close' onClick={handleModalCancel}></button>
              </div>
              <input
                type='text'
                className='form-control mb-3'
                placeholder='Search products...'
                value={modalSearch}
                onChange={(e) => handleModalSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '0' }}>
              {modalResults.length > 0 ? (
                modalResults.map((product) => {
                  const isSelected = selectedProducts.some((p) => p._id === product._id)
                  const alreadyInCategory = products?.products?.some((p) => p._id === product._id)
                  return (
                    <div
                      key={product._id}
                      className='d-flex align-items-center px-4 py-2'
                      style={{
                        cursor: alreadyInCategory ? 'default' : 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: alreadyInCategory ? '#f8f9fa' : isSelected ? '#f0f7ff' : 'transparent',
                        opacity: alreadyInCategory ? 0.7 : 1,
                      }}
                      onClick={() => !alreadyInCategory && toggleProductSelection(product)}
                    >
                      {alreadyInCategory ? (
                        <i className='fas fa-check-circle me-3' style={{ color: '#28a745', fontSize: '18px', minWidth: '18px' }}></i>
                      ) : (
                      <input
                        type='checkbox'
                        className='form-check-input me-3'
                        checked={isSelected}
                        readOnly
                        style={{ minWidth: '18px' }}
                      />
                      )}
                      {product.image && product.image[0] ? (
                        <img
                          src={`${apiUrl}/${product.image[0]}`}
                          alt=''
                          style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', marginRight: '12px' }}
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                      ) : (
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '6px', marginRight: '12px',
                          backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <i className='fas fa-image' style={{ color: '#ccc' }}></i>
                        </div>
                      )}
                      <span style={{ fontSize: '14px' }}>{product.name}</span>
                    </div>
                  )
                })
              ) : modalSearch.length >= 2 ? (
                <p className='text-center text-muted py-4'>No products found</p>
              ) : (
                <p className='text-center text-muted py-4'>Type to search products</p>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e5e5' }}
              className='d-flex justify-content-between align-items-center'
            >
              <span className='text-muted' style={{ fontSize: '14px' }}>
                {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
              </span>
              <div>
                <button className='btn btn-outline-secondary me-2' onClick={handleModalCancel}>
                  Cancel
                </button>
                <button
                  className='btn btn-dark'
                  onClick={handleModalDone}
                  disabled={selectedProducts.length === 0}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  )
}

export default CategoryEditScreen
